import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ExternalLink } from "lucide-react";
import { SITE, NAV_LINKS } from "../lib/site";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "bg-background/90 backdrop-blur-xl border-b border-border"
          : "bg-background/40 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between gap-6">
        <NavLink to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
          <img
            src={SITE.logo}
            alt="BeGreat Basketball Training"
            className="h-10 md:h-12 w-auto"
            style={{ mixBlendMode: "multiply" }}
          />
        </NavLink>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {NAV_LINKS.map((l) =>
            l.external ? (
              <a
                key={l.id}
                href={SITE.clothingUrl}
                target="_blank"
                rel="noreferrer"
                data-testid={`nav-link-${l.id}`}
                className="text-[11px] xl:text-xs font-bold uppercase tracking-[0.2em] text-foreground/80 hover:text-primary transition-colors inline-flex items-center gap-1"
              >
                {l.label} <ExternalLink size={11} />
              </a>
            ) : (
              <NavLink
                key={l.id}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `text-[11px] xl:text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
                    isActive ? "text-primary" : "text-foreground/80 hover:text-primary"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <a
          href={SITE.phoneHref}
          data-testid="nav-phone"
          className="hidden"
        >
          <Phone size={14} /> {SITE.phone}
        </a>
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 -mr-2"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden bg-background border-t border-border"
            data-testid="nav-mobile-panel"
          >
            <div className="px-6 py-6 flex flex-col gap-3">
              {NAV_LINKS.map((l) =>
                l.external ? (
                  <a
                    key={l.id}
                    href={SITE.clothingUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-2xl font-display uppercase tracking-tight inline-flex items-center gap-2"
                  >
                    {l.label} <ExternalLink size={16} />
                  </a>
                ) : (
                  <NavLink
                    key={l.id}
                    to={l.to}
                    end={l.to === "/"}
                    className={({ isActive }) =>
                      `text-2xl font-display uppercase tracking-tight ${isActive ? "text-primary" : "hover:text-primary"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                )
              )}
              <a
                href={SITE.phoneHref}
                className="hidden"
              >
                <Phone size={14} /> {SITE.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
